import { useState } from 'react';
import { Box, Typography, ClickAwayListener, Tooltip, IconButton, Chip, useTheme, darken } from '@mui/material';
import { MdInfoOutline } from 'react-icons/md';
import CopyIcon from 'assets/icons/CopyIcon';
import { BACKEND_URL } from 'env';
import { SupportedProvider } from '../utils/types';

const WEBHOOK_EVENTS = ['message-delivered', 'message-failed'];

const WEBHOOK_CONFIG: Partial<Record<SupportedProvider, { title: string; instruction: string; path: string }>> = {
  bandwidth: {
    title: 'Bandwidth Webhook Configuration',
    instruction: 'To complete your Bandwidth integration, create a webhook in your Bandwidth Dashboard:',
    path: '/bandwidth/sms/status'
  },
  twilio: {
    title: 'Twilio Webhook Configuration',
    instruction: 'To complete your Twilio integration, create a webhook in your Twilio Dashboard:',
    path: '/webhooks/twilio/sms'
  }
};

interface Props {
  provider: SupportedProvider;
}

export default function WebhookConfigInfo({ provider }: Props) {
  const theme = useTheme();
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const config = WEBHOOK_CONFIG[provider];

  if (!config) return null;

  const webhookUrl = `${BACKEND_URL}${config.path}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(webhookUrl);
    setCopyTooltipOpen(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${darken(theme.palette.primary.light, 0.1)}`,
        borderRadius: 1,
        p: { xs: 1.5, sm: 2 },
        my: 2
      }}
    >
      <Box display='flex' flexDirection='row' alignItems='center' gap={1} mb={1}>
        <Box
          component='span'
          sx={{
            width: { xs: 20, sm: 24 },
            height: { xs: 20, sm: 24 },
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <MdInfoOutline size={18} />
        </Box>
        <Typography
          fontWeight={500}
          color={theme.palette.primary.main}
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {config.title}
        </Typography>
      </Box>
      <Typography color={theme.palette.primary.main} sx={{ fontSize: { xs: '12px', sm: '14px' } }} mb={1}>
        {config.instruction}
      </Typography>

      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        borderRadius={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={{ xs: 1, sm: 0 }}
        sx={{ backgroundColor: 'white', px: { xs: 1, sm: 1.5 }, py: 1 }}
      >
        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 0.5, sm: '3px' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
        >
          <Typography
            color={theme.palette.primary.main}
            fontWeight={600}
            sx={{ fontSize: { xs: '12px', sm: '14px' } }}
            noWrap
          >
            Webhook URL :
          </Typography>
          <Typography
            component='a'
            href={webhookUrl}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
              color: theme.palette.primary.main,
              wordBreak: 'break-all',
              textDecoration: 'underline'
            }}
          >
            {webhookUrl}
          </Typography>
        </Box>

        <ClickAwayListener onClickAway={() => setCopyTooltipOpen(false)}>
          <Tooltip
            onClose={() => setCopyTooltipOpen(false)}
            open={copyTooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement='top'
            title='Copied'
          >
            <IconButton onClick={handleCopy} size='small' sx={{ flexShrink: 0 }}>
              <Box color={theme.palette.primary.main}>
                <CopyIcon />
              </Box>
            </IconButton>
          </Tooltip>
        </ClickAwayListener>
      </Box>

      <Typography mt={1} color={theme.palette.primary.main} sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Make sure to select and activate the following events:
      </Typography>
      <Box display='flex' flexWrap='wrap' mt={1} gap={1}>
        {WEBHOOK_EVENTS.map((event) => (
          <Chip
            key={event}
            label={event}
            size='small'
            sx={{
              border: `1px solid ${theme.palette.primary.main}`,
              bgcolor: 'white',
              color: theme.palette.primary.main,
              borderRadius: '40px',
              textTransform: 'none'
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
